import {Component, ReactNode} from 'react';
import {Navigate} from 'react-router-dom';
import {AxiosError} from 'axios';

import {isDev} from '@zzng/common/utils/common';

import {ErrorCode} from '@/shared/constants/errorCode';
import {SHARED_ROUTES} from '@/shared/constants/routes';
import {ValueOf} from '@/shared/utils/typescript';

interface ErrorBoundaryProps {
  pathByErrorCodeOverrides?: Record<string, string>;
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

const sharedPathByErrorCode: Record<ErrorCode, ValueOf<typeof SHARED_ROUTES>> = {
  [ErrorCode.UNDER_MAINTENANCE]: SHARED_ROUTES.UNDER_MAINTENANCE,
  [ErrorCode.INVALID_LOGIN_STATUS]: SHARED_ROUTES.LOGIN_ERROR,
  [ErrorCode.INVALID_DEVICE]: SHARED_ROUTES.INVALID_DEVICE
};

export default class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {hasError: false};
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return {hasError: true, error};
  }

  handleError = (event: ErrorEvent) => {
    /** AxiosError는 다른 리스너(react-query, errorElement, unhandledrejection)에서 처리하므로 무시 */
    if (event.error instanceof AxiosError) {
      return;
    }
    this.setState({hasError: true, error: event.error});
  };

  handleUnhandledRejection = (event: PromiseRejectionEvent) => {
    this.setState({hasError: true, error: event.reason});
  };

  handleOffline = () => {
    this.setState({hasError: true, error: new AxiosError('Network Error', AxiosError.ERR_NETWORK)});
  };

  /** React 렌더링 에러 이외에 전역 에러 처리 */
  componentDidMount() {
    window.addEventListener('error', this.handleError);
    window.addEventListener('unhandledrejection', this.handleUnhandledRejection);
    window.addEventListener('offline', this.handleOffline);
  }

  /** 메모리 누수 방지 */
  componentWillUnmount() {
    window.removeEventListener('error', this.handleError);
    window.removeEventListener('unhandledrejection', this.handleUnhandledRejection);
    window.removeEventListener('offline', this.handleOffline);
  }

  render() {
    const {hasError, error} = this.state;

    if (!hasError) {
      return this.props.children;
    }

    if (isDev) {
      console.error('error', error);
    }

    if (error instanceof AxiosError) {
      if (error.code === AxiosError.ERR_CANCELED) {
        return this.props.children;
      }

      if (error.code === AxiosError.ERR_NETWORK) {
        return <Navigate to={{pathname: SHARED_ROUTES.NETWORK_ERROR, search: `?returnUrl=${location.href}`}} replace />;
      }

      if (error.response?.status === 401) {
        return <Navigate to={SHARED_ROUTES.LOGIN_ERROR} replace />;
      }

      const {code, message} = error.response?.data ?? {};
      const pathByErrorCode = {...sharedPathByErrorCode, ...this.props.pathByErrorCodeOverrides};

      if (code in pathByErrorCode) {
        const path = pathByErrorCode[code as keyof typeof pathByErrorCode];
        return <Navigate to={path} state={{message}} replace />;
      }
    }

    return <Navigate to={SHARED_ROUTES.SERVER_ERROR} replace />;
  }
}
